import { Typography, notification } from 'antd';
import { AxiosError } from 'axios';
import { useRouter } from 'next/router';
import { ReactNode, useCallback, useEffect } from 'react';
import { FieldPath, FormProvider, SubmitHandler, UseFormHandleSubmit, useForm } from 'react-hook-form';
import { Trans, getI18n, useTranslation } from 'react-i18next';
import { EndUserRequestFormValues } from '../../../types/form';
import { endUserRequestFormDefaultValues } from '../../constants/forms/ticketForm';
import { FORM_FIELD_KEY } from '../../containers/Pages/TicketPages/TicketPagesContentLayout/AgentFormActiveFieldsContainer';
import {
    subscriptionAttachmentLimitMap,
    subscriptionLimitMap
} from '../../containers/subscription/SubscriptionController';
import useEndUserFormFields from '../../hooks/end-user-form/useEndUserFormFields';
import { getUIFriendlyTicketFieldDefinitionList } from '../../hooks/ticketDetailPage/useTicketFieldDefinitionList';
import useValidateOnLanguageChange from '../../hooks/useValidateOnLanguageChange';
import { FieldDefinitionMap, TICKET_FORM_FIELDS_KEY, TicketSystemFieldsKeyMap } from '../../model/types';
import { createEndUserRequest } from '../../services/end-user-form/endUserServices';
import {
    controlledUserFormValue,
    parseEmailFromSerializedValue,
    parseFullNameFromSerializedUntrustedEndUserValue,
    parsePhoneNumberFromSerializedValue
} from '../../utils/forms/formHelper.parser';
import requestWithMessage from '../../utils/requestWithMessage';
import { PRIVATE_CLASS_NAME } from '../QuillGrispiRichTextEditor/constants/richTextEditor';
import { hasFieldUserValueType } from './CreateTicketForm';

function generateCreatorData(formValues: EndUserRequestFormValues) {
    const creator: EndUserRequestFormValues['comment']['creator'] = [];

    const endUserValue = formValues.fields['ts$untrusted_end_user'] as string;
    const email = parseEmailFromSerializedValue(endUserValue);
    const fullName = parseFullNameFromSerializedUntrustedEndUserValue(endUserValue);
    const phone = parsePhoneNumberFromSerializedValue(endUserValue);

    email && creator.push({ key: 'us.email', value: email });
    phone && creator.push({ key: 'us.phone', value: phone });
    fullName && creator.push({ key: 'us.full_name', value: fullName });

    return creator;
}

const getEndUserRequestPostDataFromFormValues = (
    formValues: EndUserRequestFormValues,
    fieldListMap: FieldDefinitionMap
): FormData => {
    const formData = new FormData();
    const { fields, ...rest } = formValues;
    const { [TicketSystemFieldsKeyMap.attachments]: attachments, ...restFields } = fields;

    const postDataFields: { key: string; value: string | number }[] = [];
    Object.entries(restFields).forEach(([key, value]) => {
        const postKey = key.replaceAll('$', '.');

        if (value) {
            const postValue =
                typeof value === 'string'
                    ? hasFieldUserValueType(fieldListMap[postKey])
                        ? controlledUserFormValue(value)
                        : value
                    : value;

            postDataFields.push({
                key: postKey,
                value: postValue as string | number
            });
        }
    });

    const endUserInfoDOMString = generateEndUserInformationDOMString(formValues);
    const commentBody = `${endUserInfoDOMString}${rest.comment.body}`;

    const ticketFromClient = {
        fields: postDataFields,
        ...rest,
        comment: {
            ...rest.comment,
            body: commentBody.replaceAll(`"`, `'`),
            creator: generateCreatorData(formValues)
        } // to prevent JSON.stringify method to get confused
    };
    formData.append('ticketFromClientString', JSON.stringify(ticketFromClient));

    if (Array.isArray(attachments) && attachments?.length > 0) {
        attachments.forEach((file) => {
            formData.append('attachments', file.originFileObj as any, file.name);
        });
    }

    return formData;
};

function generateEndUserInformationDOMString(formValues: EndUserRequestFormValues) {
    const t = (text: string) => getI18n().t(text, { ns: 'forms' });

    const endUserValue = formValues.fields['ts$untrusted_end_user'] as string;
    const fullName = parseFullNameFromSerializedUntrustedEndUserValue(endUserValue);
    const email = parseEmailFromSerializedValue(endUserValue);
    const phone = parsePhoneNumberFromSerializedValue(endUserValue);

    const fullNameColumnTitle = t('form.fullName.label');
    const emailColumnTitle = t('form.email.label');
    const phoneColumnTitle = t('form.phone.shortLabel');

    const divStyle = 'display:flex';
    const columnSpanStyle =
        'border-bottom:1px solid;border-left:1px solid;display:inline-block;width:12ch;text-align:end;padding:0.5ch;padding-right:1ch';
    const infoSpanStyle =
        'border:1px solid;border-top:none;display:inline-block;width:30ch;padding:0.5ch;padding-left:1ch';
    const hrStyle = 'height:0px !important';

    return `<div class='${PRIVATE_CLASS_NAME}'style='${divStyle}'><div class='${PRIVATE_CLASS_NAME}'style='${columnSpanStyle};border-top:1px solid'>${fullNameColumnTitle} :</div><div class='${PRIVATE_CLASS_NAME}'style='${infoSpanStyle};border-top:1px solid'>${fullName}</div></div><div class='${PRIVATE_CLASS_NAME}'style='${divStyle}'><div class='${PRIVATE_CLASS_NAME}'style='${columnSpanStyle}'>${emailColumnTitle} :</div><div class='${PRIVATE_CLASS_NAME}'style='${infoSpanStyle}'>${email}</div></div><div class='${PRIVATE_CLASS_NAME}'style='${divStyle}'><div class='${PRIVATE_CLASS_NAME}'style='${columnSpanStyle}'>${phoneColumnTitle} :</div><div class='${PRIVATE_CLASS_NAME}'style='${infoSpanStyle}'>${phone}</div></div><hr class='${PRIVATE_CLASS_NAME}'style='${hrStyle}'/>`;
}

const EndUserForm = ({ children }: { children: ReactNode }) => {
    const { t } = useTranslation('forms');
    const {
        asPath,
        push,
        query: { userFormId }
    } = useRouter();

    const safeFormId = !Number.isNaN(+userFormId) ? +userFormId : null;
    const { data } = useEndUserFormFields();
    const fieldList = getUIFriendlyTicketFieldDefinitionList(data);

    const [api, contextHolder] = notification.useNotification();

    const openAttachmentErrorNotification = useCallback(
        (error: Error) => {
            const isCountError = error?.message?.includes('COUNT');
            return api.error({
                message: (
                    <Typography.Title level={4}>
                        <Trans ns="subscription" i18nKey={`attachmentInfoTitle`} />
                    </Typography.Title>
                ),
                description: (
                    <Typography.Text strong>
                        <Trans
                            ns="subscription"
                            i18nKey={isCountError ? 'attachmentInfoCount' : `attachmentInfoSize`}
                        />
                    </Typography.Text>
                ),
                duration: 10
            });
        },
        [api]
    );
    const openLimitErrorNotification = useCallback(() => {
        return api.error({
            message: (
                <Typography.Title level={4}>
                    <Trans ns="subscription" i18nKey={`limitInfoTitle`} />
                </Typography.Title>
            ),
            description: (
                <Typography.Text strong>
                    <Trans ns="subscription" i18nKey={'limitInfoText'} />
                </Typography.Text>
            ),
            duration: 10
        });
    }, [api]);

    const methods = useForm<EndUserRequestFormValues>({
        mode: 'onChange',
        defaultValues: {
            ...endUserRequestFormDefaultValues,
            [TICKET_FORM_FIELDS_KEY]: {
                ...endUserRequestFormDefaultValues[TICKET_FORM_FIELDS_KEY],
                [TicketSystemFieldsKeyMap.formId]: safeFormId
            }
        },
        shouldFocusError: true
    });

    const {
        formState: { errors },
        handleSubmit,
        getValues,
        trigger,
        setValue,
        reset
    } = methods;
    useValidateOnLanguageChange(() => trigger(Object.keys(errors) as FieldPath<EndUserRequestFormValues>[]));

    useEffect(() => {
        if (typeof userFormId === 'string' && !Number.isNaN(+userFormId)) {
            getValues(FORM_FIELD_KEY) !== +userFormId && setValue(FORM_FIELD_KEY, +userFormId);
        }
    }, [getValues, setValue, userFormId]);

    const onValid: SubmitHandler<EndUserRequestFormValues> = useCallback(
        async (formValues) => {
            const postData = getEndUserRequestPostDataFromFormValues(formValues, fieldList.listMap);
            await requestWithMessage({
                errorConfig: {
                    onApiRequestError: async (error: AxiosError<any>) => {
                        const limitKey = error?.response?.data?.limitKey;
                        if (limitKey) {
                            const attachment = Object.keys(subscriptionAttachmentLimitMap).find(
                                (attachment) => typeof limitKey === 'string' && limitKey?.includes(attachment)
                            );
                            if (attachment) {
                                openAttachmentErrorNotification(error);
                                return;
                            }
                            const limit = Object.keys(subscriptionLimitMap).find(
                                (limit) => typeof limitKey === 'string' && limitKey?.includes(limit)
                            );
                            if (limit) {
                                openLimitErrorNotification();
                                return;
                            }
                        }
                    },
                    showAutoErrorMessage: false,
                    isPublicApiRequest: true
                },
                key: 'end-user-request',
                startMessage: t('endUserForm.message.onRequestStart'),
                apiRequest: async () => {
                    const ticketKey = await createEndUserRequest(postData);
                    push(`${asPath}/${ticketKey}`);
                }
            });
        },
        [asPath, fieldList.listMap, openAttachmentErrorNotification, openLimitErrorNotification, push, t]
    );

    const onSubmit = useCallback(
        (handleOnSubmit: ReturnType<UseFormHandleSubmit<EndUserRequestFormValues>>) => (e?: React.BaseSyntheticEvent) =>
            handleOnSubmit(e).catch(() => {
                //HACK: This is a workaround for issue with react-hook-form that is 'isSubmitting' value remains true on api-errors.
                reset({}, { keepValues: true, keepIsValid: true });
                // console.error('EndUserForm, onValid, submit error:', e);
            }),
        [reset]
    );

    return (
        <FormProvider {...methods}>
            <form onSubmit={onSubmit(handleSubmit(onValid))}>{children}</form>
            {contextHolder}
        </FormProvider>
    );
};
export default EndUserForm;
